import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import { PreferencesSync } from './lib/preferences';
import { ThemeProvider } from './lib/theme';
import { ToastProvider } from './components/ui';
import './index.css';

const ACCESS_CHANGED = new Set(['GYM_SUSPENDED', 'OFF_SITE', 'OFF_DUTY']);

function reloadUserWhenAccessChanges(err: unknown) {
  if (ACCESS_CHANGED.has((err as { code?: string }).code ?? '')) void queryClient.invalidateQueries({ queryKey: ['me'] });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reloadUserWhenAccessChanges }),
  mutationCache: new MutationCache({ onError: reloadUserWhenAccessChanges }),
  defaultOptions: {
    queries: { staleTime: 15_000, retry: (count, err) => (err as { status?: number }).status === 401 || (err as { status?: number }).status === 403 ? false : count < 2 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <I18nProvider>
          <ThemeProvider>
            <ToastProvider>
              <AuthProvider>
                <PreferencesSync />
                <App />
              </AuthProvider>
            </ToastProvider>
          </ThemeProvider>
        </I18nProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
